<template>
  <div class="contents shinsei">
    <h2>申請　詳細</h2>

    <div class="container">
      <template v-if="isLoading || isSaving">
        <loading :isLoading="isLoading" :message="'読み込み中...'"></loading>
      </template>

      <template v-else>
        <div class="container-info">
          <h3 v-if="form.is_soumu_changed" class="title-soumu">総務修正による申請</h3>
          <div class="container-base">
            <div class="col-item">
            </div>
            <div class="col-item"><b class="title">申請日</b><span>{{ form?.shinsei_date }}</span></div>
            <div class="col-item"><b class="title">社員番号</b><span>{{ form?.staff_code }}</span></div>
            <div class="col-item"><b class="title">氏名</b><span>{{ form?.staff_name }}</span></div>

            <div class="maininfo"
              :class="{ 'cancel-title': form.syounin_status_type == OptionConst.SYOUNIN_STATUS.CANCEL }">
              <component :is="form.shinsei_type_str" :form="form" :workTime="workTime" ref="child" />
            </div>
            <!-- /maininfo -->

            <div v-if="!form.is_soumu_changed">
              <h3>申請経路</h3>
              <scroll-table class="route-table">
                <thead>
                  <th class="aprover"></th>
                  <th class="name">氏名</th>
                  <th class="result">承認結果</th>
                  <th class="comment">コメント</th>
                  <th class="date-time">日時</th>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in flow" :key="index">
                    <td v-show="item.is_syounin">承認者</td>
                    <td v-show="!item.is_syounin">確認者</td>
                    <td>{{ item.staff_name }}</td>
                    <template v-if="isControl(item)">
                      <template v-if="isApprover">
                        <td class="btn-area">
                          <p class="btn-temporary-save">
                            <button type="button" class="btn success" @click="onSave(SyouninStatus.APPROVED)"
                              :disabled="isLoading || isSaving">
                              <i class="fa-solid fa-inbox"></i>承認
                            </button>
                            <button type="button" class="btn danger" @click="onSave(SyouninStatus.DENIED)"
                              :disabled="isLoading || isSaving">
                              <i class="fa-solid fa-inbox"></i>否認
                            </button>
                          </p>
                        </td>
                      </template>

                      <template v-if="isConfirmer">
                        <td class="btn-area">
                          <p class="btn-temporary-save">
                            <button type="button" class="btn success" @click="saveCheck"
                              :disabled="isLoading || isSaving">
                              <i class="fa-solid fa-check"></i>確認
                            </button>
                          </p>
                        </td>
                      </template>

                      <template v-if="!isApprover && !isConfirmer">
                        <td>{{ approvalTypeStr(item.status_type, item.is_syounin) }}</td>
                      </template>
                    </template>

                    <template v-else>
                      <td>{{ approvalTypeStr(item.status_type, item.is_syounin) }}</td>
                    </template>
                    <td class="pre">{{ item.status_notes }}</td>
                    <td>{{ item.status_type !== SyouninStatus.WAIT ? dateTimeFormat(item.updated_at) : '' }}</td>
                  </tr>

                  <tr>
                    <td>確認者</td>
                    <td>八尾坂社長</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                  <tr>
                    <td>確認者</td>
                    <td>山本部長</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                </tbody>
              </scroll-table>
            </div>

            <div class="save-buttons" v-if="form.user_id == user?.user_id">
              <button type="button" class="btn warning" @click="again" :disabled="isLoading || isUpdating"
                v-show="!(form.syounin_status_type == SyouninStatus.APPROVED || form.syounin_status_type == SyouninStatus.CANCEL)">

                <template v-if="!isUpdating">
                  <i class="fa-solid fa-edit"></i>{{ btnName }}
                </template>
              </button>
              <button type="button" class="btn danger" @click="saveCancel" :disabled="isLoading || isCanceling"
                v-show="form.syounin_status_type == SyouninStatus.WAIT">

                <template v-if="isCanceling">
                  <i class="fa-solid fa-spinner fa-spin-pulse"></i>処理中…
                </template>

                <template v-else>
                  <i class="fa-solid fa-minus"></i>申請取消
                </template>
              </button>
            </div>
            <div class="save-buttons" v-else-if="isApprovedAndHasPermission">
              <button type="button" class="btn danger" @click="saveCancel" :disabled="isLoading || isCanceling">

                <template v-if="isCanceling">
                  <i class="fa-solid fa-spinner fa-spin-pulse"></i>処理中…
                </template>

                <template v-else>
                  <i class="fa-solid fa-minus"></i>申請取消
                </template>
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="button-container">
      <p class="prev-page">
        <a @click="back">戻る
        </a>
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted } from "vue";
import { useForm } from "@/composable/shinsei/typelist/form";
import { useInit } from "@/composable/shinsei/typelist/init";
import { useCancel } from "@/composable/shinsei/detail/cancel";
import { useApproval } from "@/composable/shinsei/approval";
import { useYuukyuu } from "@/composable/shinsei/yuukyuu/yuukyuu";
import { dateFormat } from "@/filters/common";
import { DialogService } from '@/service/DialogService';
import { DateUtil } from '@/utils/DateUtil';
import { RouterConst } from "@/const/Router";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from '@/store/auth';
import { Flow } from "@/types/SyouninGroup";
import { PermissionConst } from '@/const/Permission';
import Jikangai from "../detail/parts/Jikangai.vue";
import Furikyuu from "../detail/parts/Furikyuu.vue";
import Yuukyuu from "../detail/parts/Yuukyuu.vue";
import Kekkin from "../detail/parts/Kekkin.vue";
import Soutai from "../detail/parts/Soutai.vue";
import Chikoku from "../detail/parts/Chikoku.vue";
import Kibiki from "../detail/parts/Kibiki.vue";
import Idou from "../detail/parts/Idou.vue";
import Kosyoku from "../detail/parts/Kosyoku.vue";
import Nittyoku from "./parts/Nittyoku.vue";
import Kiken from "../detail/parts/Kiken.vue";
import Sonota from "../detail/parts/Sonota.vue";
import DakokuSyuusei from "../detail/parts/DakokuSyuusei.vue";
import KyuukeiJikangai from "../detail/parts/KyuukeiJikangai.vue";
import OptionConst from '@/const/Option';
import Swal from 'sweetalert2';
import { KoyouKeiyakuTimeForDetail } from "@/types/Shinsei";

export default defineComponent({
  setup() {
    // 初期化
    const { form, id } = useInit()
    const { isLoading, isError, getShinseiInfo, getUser, flow, options, user, getShinseiSyouninFlow } = useForm()
    const { getZanYuukyuu, zanYuukyuu } = useYuukyuu();
    const { isCanceling, cancel } = useCancel()
    const { isSaving, approval, check } = useApproval()
    const isUpdating = ref(false)
    const authStore = useAuthStore()
    const route = useRoute()
    const router = useRouter()
    const controllerData = ref<Flow | null>(null)
    const child = ref()
    const workTime = ref<KoyouKeiyakuTimeForDetail[] | null>(null)

    /** 初期処理 */
    onMounted(async () => {
      // 申請情報
      const res = await getShinseiInfo(form, id)
      form.value = res.data;
      form.value.shinsei_type_str = res.shinsei_type;
      workTime.value = res.workTimes
      // 承認フローの取得
      flow.value = await getShinseiSyouninFlow(id, Number(form.value.shinsei_type), Number(form.value.user_id))
      // ユーザー情報の取得
      await getUser()
      // 有給休暇残日数の取得
      zanYuukyuu.value = await getZanYuukyuu(Number(form.value.user_id))
    })

    /** 承認者 */
    const isApprover = computed(() => {
      if (form.value.syounin_status_type != OptionConst.SYOUNIN_STATUS.WAIT) {
        return false
      }
      if (controllerData.value == null) {
        return false
      }
      if (!controllerData.value.is_syounin) {
        return false
      }
      return controllerData.value.is_next
    })

    const isApprovedAndHasPermission = computed(() => {
      if (form.value.syounin_status_type != OptionConst.SYOUNIN_STATUS.APPROVED) {
        return false
      }
      if (form.value.shinsei_type_str == OptionConst.SHINSEI_TYPE.DAKOKU_SYUUSEI) {
        return false
      }
      return authStore.hasPermission(PermissionConst.SHINSEI_CANCEL_ALL) ||
        authStore.hasPermission(PermissionConst.SHINSEI_CANCEL_SYOUNIN) ||
        authStore.hasPermission(PermissionConst.SHINSEI_CANCEL_SYOZOKU)
    })

    /** 確認者 */
    const isConfirmer = computed(() => {
      if (form.value.syounin_status_type != OptionConst.SYOUNIN_STATUS.APPROVED) {
        return false
      }
      if (controllerData.value == null) {
        return false
      }
      return controllerData.value.is_syounin == false
    })

    /** ボタン操作の可否 */
    const isControl = (val: Flow) => {
      if (val.user_id != user.value?.user_id) {
        return false
      }
      if (val.status_type != OptionConst.SYOUNIN_STATUS.WAIT) {
        return false
      }
      controllerData.value = flow.value.find(v => v.user_id === user.value?.user_id) ?? null
      return true
    }

    /** ボタン名称 */
    const btnName = computed(() => {
      if (!id) {
        return '申請'
      }
      if (flow.value.some(v => v.status_type != OptionConst.SYOUNIN_STATUS.WAIT)) {
        return '再申請'
      } else {
        return '修正'
      }
    })

    /** 承認結果の表示 */
    /** val: status_type 承認ステータス, isSyounin: 承認者かどうかフラグ */
    const approvalTypeStr = (val: number, isSyounin: Boolean) => {
      if (form.value.syounin_status_type == OptionConst.SYOUNIN_STATUS.DENIED
        && val == OptionConst.SYOUNIN_STATUS.WAIT) {
        return ''
      }

      if (isSyounin) {
        // 承認者の場合
        return OptionConst.SYOUNIN_STATUS_OPTION.find(opt => opt.val === val)?.label
      } else {
        // 確認者の場合
        // return OptionConst.KAKUNIN_STATUS_OPTION.find(opt => opt.val === val)?.label
        return '-' // MEDICA_AS-1208 対応
      }
    }

    /** 日時フォーマット */
    const dateTimeFormat = (val: string | Date | null) => {
      return val ? DateUtil.format(val, 'YYYY/MM/DD HH:mm') : ''
    }

    /** 申請取消処理 */
    const saveCancel = async () => {
      DialogService.question({
        text: '申請を取り消しますか？',
        confirmButtonText: 'はい',
        showCancelButton: true,
        cancelButtonText: 'いいえ',
      }).then(async (result) => {
        if (!result.isConfirmed) {
          return
        }
        await cancel(id)
        router.push({ name: RouterConst.SHINSEI_LIST })
      })
    }

    const onSave = async (val: number) => {
      let isSave = true
      if (form.value.shinsei_type_str == OptionConst.SHINSEI_TYPE.JIKANGAI) {
        isSave = await child.value.alertStartTime()
      }
      if (isSave) {
        saveNew(val)
      }
    }

    /** 新規登録処理 */
    const saveNew = async (val: number) => {
      let opt = {}
      if (val === OptionConst.SYOUNIN_STATUS.APPROVED) {
        opt = {
          customClass: {
            input: 'popup-textarea',
          },
          text: '承認しますか？',
          confirmButtonText: 'はい',
          showCancelButton: true,
          cancelButtonText: 'いいえ',
          input: 'textarea',
          inputPlaceholder: '承認理由を入力(任意)',
          preConfirm: () => {
            if (form.value.shinsei_type_str == OptionConst.SHINSEI_TYPE.YUUKYUU &&
              zanYuukyuu.value.zan_yuukyuu < 0) {
              return Swal.showValidationMessage('有給休暇残日数がありません');
            }
            const value = Swal.getInput()?.value
            if (value) {
              return value
            }
            return ''
          }
        }
      } else {
        opt = {
          text: '否認しますか？',
          confirmButtonText: 'はい',
          showCancelButton: true,
          cancelButtonText: 'いいえ',
          input: 'textarea',
          inputPlaceholder: '否認理由を入力(必須)',
          preConfirm: () => {
            const value = Swal.getInput()?.value
            if (!value) {
              return Swal.showValidationMessage('否認理由は必須です')
            }
            return value
          }
        }
      }

      DialogService.question(opt, true).then(async (result) => {
        if (result.isConfirmed) {
          const notes = result.value as string
          const resFlow = await approval(form.value, id, val, notes)
          if (resFlow) {
            flow.value = resFlow
          }
        }
      })
    };

    /** 確認者による承認処理） */
    const saveCheck = async () => {
      await DialogService.question({
        customClass: {
          input: 'popup-textarea',
        },
        text: '承認しますか？',
        confirmButtonText: 'はい',
        showCancelButton: true,
        cancelButtonText: 'いいえ',
        input: 'textarea',
        inputPlaceholder: 'コメントを入力(任意)',
        preConfirm: () => {
          const value = Swal.getInput()?.value
          return value ?? ''
        }
      }).then(async (result) => {
        if (result.isConfirmed) {
          const notes = result.value as string
          const syouninType = OptionConst.SHINSEI_CODE_FOR_FLOW_TYPES.find(v => v.code == form.value.shinsei_type)?.flow_type ?? OptionConst.SYOUNIN_FLOW_TYPES.DEFAULT
          await check(id, syouninType, notes)
          flow.value = await getShinseiSyouninFlow(id, Number(form.value.shinsei_type), Number(form.value.user_id))
        }
      })
    }

    /** 再申請処理 */
    const again = () => {
      if (form.value.syounin_status_type == OptionConst.SYOUNIN_STATUS.DENIED) {
        router.push({ name: RouterConst.SHINSEI_AGAIN, params: { type: form.value.shinsei_type_str, id: id } })
      } else {
        router.push({ name: RouterConst.SHINSEI_EDIT, params: { type: form.value.shinsei_type_str, id: id } })
      }
    }

    const back = () => {
      const from = route.query.from ? route.query.from : null

      if (from === RouterConst.KINMU_LIST || from === RouterConst.HAKEN_KINMU_LIST) {
        if (route.query.year && route.query.month && route.query.syuugyou_basyo_id && route.query.user_id) {
          router.push({
            name: from,
            query: {
              year: parseInt(route.query.year as string, 10),
              month: parseInt(route.query.month as string, 10),
              mst_syuugyou_basyo_id: parseInt(route.query.syuugyou_basyo_id as string, 10),
              koyou_keiyaku_id: route.query.koyou_keiyaku_id ? parseInt(route.query.koyou_keiyaku_id as string, 10) : null,
              targetUserId: route.query.user_id ? parseInt(route.query.user_id as string, 10) : null
            }
          }
          )
        } else {
          router.push({ name: from })
        }
      } else if (from && !Array.isArray(from)) {
        router.push({ name: from })
      } else {
        router.go(-1)
      }
    }

    return {
      isLoading,
      isSaving,
      isError,
      isCanceling,
      dateFormat,
      again,
      saveCancel,
      saveCheck,
      user,
      zanYuukyuu,
      form,
      flow,
      options,
      approvalTypeStr,
      dateTimeFormat,
      isApprover,
      isApprovedAndHasPermission,
      isConfirmer,
      isUpdating,
      "KoyouKeitaiType": OptionConst.KOYOU_KEITAI_TYPE_OPTION,
      "SyouninStatus": OptionConst.SYOUNIN_STATUS,
      RouterConst,
      OptionConst: OptionConst,
      isControl,
      btnName,
      router,
      onSave,
      workTime,
      child,
      back
    };
  },
  components: {
    jikangai: Jikangai,
    furikyuu: Furikyuu,
    yuukyuu: Yuukyuu,
    kekkin: Kekkin,
    soutai: Soutai,
    chikoku: Chikoku,
    kibiki: Kibiki,
    idou: Idou,
    kosyoku: Kosyoku,
    nittyoku: Nittyoku,
    kiken: Kiken,
    sonotateate: Sonota,
    dakoku_syuusei: DakokuSyuusei,
    kyuukei_jikangai: KyuukeiJikangai,
  },
});
</script>

<style lang="scss" scoped>
@import '@scss/components/shinsei-detail.scss';
</style>
